import classNames from "classnames";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import ConfirmationModal from "../confirmationModal";
import { useMemo, useState, useEffect, ChangeEvent } from "react";
import { AuthConfig } from "../../../../setup/context/config/authConfig";
import { PartyErrorsMap } from "../bulkUploadModal";
import { ReactComponent as DeleteResource } from "../../../../assets/images/svg/deleteResource.svg";
import {
  EFeePayee,
  ESenderRole,
} from "../../../../setup/api/services/contract/models";
import { GET_PARTY_STATUSES } from "../../../../components/contract/payeeTable/constants";
import TooltipWrapper from "../../../../components/common/TooltipWrapper";
import OutlinedButton from "../../../../components/common/OutlinedButton";
import useWindowSize from "../../../../hooks/getPageSize";
import PartyCard from "./partyCard";
import { matchPath } from "react-router-dom";
import { ROUTES } from "../../../../setup/constants";
import { isArabicOnly } from "../../../../setup/util/isArabic";
import TextInput, { InputType } from "../../../../components/forms/textInput";

export type PartyRow = {
  id: string;
  rowId?: string; // Add rowId field for error mapping
  name: string;
  phoneNumber: string;
  amount: number | string;
  role?: string;
  type: string;
  status?: string;
  fee: number | string;
  total: number | string;
  description?: string;
  image?: string;
  formAmount?: number;
  cashoutTime?: string;
  referenceNumber?: string;
};

type PartyTableProps = {
  detailsPage?: boolean;
  rows: PartyRow[];
  emptyMessage?: string;
  className?: string;
  feePayeeProp?: EFeePayee;
  handleEdit?: (partyIndex: number) => void;
  handleDelete?: (partyIndex: number) => void;
  /**
   * When rows are filtered/mapped before passing to the table (e.g. only providers),
   * provide a resolver to translate the displayed row back to the original array index
   * used by the parent form/state.
   */
  getIndexByRow?: (row: PartyRow, displayIndex: number) => number;
  /**
   * Columns visibility configuration. If omitted, sensible defaults are inferred
   * from type, detailsPage and feePayeeProp.
   */
  columns?: {
    showRole?: boolean;
    showFee?: boolean;
    showTotal?: boolean;
    showStatus?: boolean;
    showActions?: boolean;
    showInputField?: boolean;
    showCashoutTime?: boolean;
    showReferenceNumber?: boolean;
  };
  /**
   * Enable bulk selection of rows
   */
  enableBulkSelect?: boolean;
  /**
   * Callback that receives the current selected indices when selection changes
   */
  onSelectionChange?: (selectedIndices: number[]) => void;
  /**
   * Reset selection state when this value changes
   */
  resetSelectionTrigger?: number;
  /**
   * Map of party ID to field-specific errors
   */
  partyErrors?: PartyErrorsMap;
  /**
   * Input Field Props
   */
  inputFieldProps?: {
    maxAmount: number;
    handleChange: (
      e: ChangeEvent<HTMLInputElement>,
      rowId: string,
      id: string
    ) => void;
  };
};

const PartyTable = ({
  detailsPage = false,
  rows,
  className,
  handleDelete,
  handleEdit,
  feePayeeProp,
  emptyMessage,
  getIndexByRow,
  columns,
  enableBulkSelect = false,
  onSelectionChange,
  resetSelectionTrigger = 0,
  partyErrors = {},
  inputFieldProps,
}: PartyTableProps) => {
  const { t } = useTranslation();
  const [pageSize] = useWindowSize();
  const clientAdminPhoneNumber = AuthConfig.getUserInfo()?.phoneNumber;
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [filterByErrors, setFilterByErrors] = useState(false);

  const isCreateMatch = matchPath(
    { path: ROUTES.CREATE_COMPLEX_CONTRACT },
    location.pathname
  );

  // Reset selection when the resetSelectionTrigger changes
  useEffect(() => {
    if (resetSelectionTrigger > 0) {
      setSelectedRows([]);
      setSelectAll(false);

      // Notify parent component that selection has been cleared
      if (onSelectionChange) {
        onSelectionChange([]);
      }
    }
  }, [resetSelectionTrigger]);

  const [isExpanded, setIsExpanded] = useState(false);

  const rowsToRender = useMemo(() => {
    if (rows.length <= 3 || isExpanded) return rows;
    return rows.slice(0, 3);
  }, [rows, isExpanded]);

  const toggleIsExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  const type = rows?.[0]?.type;
  const feePayee = feePayeeProp?.toLowerCase();
  // Resolve final columns visibility combining explicit config with defaults
  const showRole = columns?.showRole ?? (type === "provider" && !detailsPage);
  const showFee = columns?.showFee ?? type === feePayee;
  const showTotal = columns?.showTotal ?? type === feePayee;
  const showActions = columns?.showActions ?? !detailsPage;
  const showStatus = columns?.showStatus ?? detailsPage;
  const showInputField = columns?.showInputField;
  const showCashoutTime = columns?.showCashoutTime;
  const showReferenceNumber = columns?.showReferenceNumber;

  // Check if there are any errors and calculate total error count
  const hasErrors = Object.keys(partyErrors).length > 0;
  const totalErrorCount = Object.values(partyErrors).reduce((count, errors) => {
    return count + Object.keys(errors).length;
  }, 0);

  // Helper to check if row has errors - use rowId if available, otherwise fall back to id
  const hasErrorsForRow = (row: PartyRow): boolean => {
    const errorKey = row.rowId || row.id;
    return (
      !!partyErrors[errorKey] && Object.keys(partyErrors[errorKey]).length > 0
    );
  };

  // Get field-specific error for a row
  const getFieldError = (row: PartyRow, field: string): string | null => {
    const errorKey = row.rowId || row.id;
    return partyErrors[errorKey] && partyErrors[errorKey][field]
      ? partyErrors[errorKey][field]
      : null;
  };

  const findPartyIndex = (partyIndex: number) => {
    const row = effectiveRows?.[partyIndex];
    if (!row) return partyIndex;
    if (getIndexByRow) return getIndexByRow(row, partyIndex);
    return partyIndex;
  };
  const partiesStatuses = GET_PARTY_STATUSES(t);

  // Filter rows if filterByErrors is enabled
  let filteredRows = rows;
  if (filterByErrors && hasErrors) {
    filteredRows = rows.filter((row) => hasErrorsForRow(row));
  }

  const effectiveRows =
    filteredRows && filteredRows.length > 0
      ? filteredRows?.map((row) => {
          return {
            ...row,
            total:
              feePayee?.toLowerCase() === "customer"
                ? +(row?.amount || 0) + +(row?.fee || 0)
                : +(row?.amount || 0) - +(row?.fee || 0),
          };
        })
      : new Array(3).fill(null).map(
          (_, i) =>
            ({
              id: `empty-${i}`,
              name: "",
              phoneNumber: "",
              amount: "",
              fee: "",
              total: "",
              role: "",
            } as PartyRow)
        );

  const sumColumn = (key: keyof Pick<PartyRow, "amount" | "fee" | "total">) => {
    return effectiveRows.reduce((acc, row) => {
      const n =
        typeof row[key] === "number"
          ? (row[key] as number)
          : parseFloat((row[key] as string) || "0");
      return acc + (isNaN(n) ? 0 : n);
    }, 0);
  };

  const effectiveEmptyMessage =
    emptyMessage || t("lbl_party_table_empty_message");

  const gridTemplate = useMemo(
    () => `
  ${enableBulkSelect ? "1fr " : ""}4fr 4fr 2fr ${showRole ? "2fr" : ""} ${
      showFee ? "2fr" : ""
    } ${showTotal ? "2fr" : ""} ${showActions ? "6fr" : ""} ${
      showInputField ? "6fr" : ""
    } ${showCashoutTime ? "4fr" : ""} ${showReferenceNumber ? "3fr" : ""} ${
      showStatus ? "2fr" : ""
    }
`,
    [
      enableBulkSelect,
      showRole,
      showFee,
      showTotal,
      showActions,
      showInputField,
      showCashoutTime,
      showReferenceNumber,
      showStatus,
    ]
  );

  // Handle bulk selection
  const handleSelectRow = (index: number) => {
    const originalIndex = findPartyIndex(index);
    setSelectedRows((prev) => {
      const newSelection = prev.includes(originalIndex)
        ? prev.filter((i) => i !== originalIndex)
        : [...prev, originalIndex];

      // Notify parent component of selection changes
      if (onSelectionChange) {
        onSelectionChange(newSelection);
      }

      return newSelection;
    });
  };

  const handleSelectAll = () => {
    let newSelection: number[] = [];

    if (!selectAll) {
      newSelection = effectiveRows
        .filter(
          (row) =>
            !row.id?.startsWith("empty") &&
            row?.phoneNumber !== clientAdminPhoneNumber
        )
        .map((_, index) => {
          if (
            effectiveRows.some(
              (row) => row?.phoneNumber === clientAdminPhoneNumber
            )
          )
            return findPartyIndex(index + 1);

          return findPartyIndex(index);
        });
    }

    // Notify parent component of selection changes
    if (onSelectionChange) {
      onSelectionChange(newSelection);
    }

    setSelectedRows(newSelection);
    setSelectAll(!selectAll);
  };
  // Toggle error filter
  const toggleErrorFilter = () => {
    setFilterByErrors((prev) => !prev);
  };

  if (pageSize < 900 && !isCreateMatch) {
    return (
      <div className={styles.list}>
        {rowsToRender.map((row) => {
          return (
            <PartyCard
              key={row.rowId}
              name={row.name}
              phone={row.phoneNumber}
              image={row.image}
              amount={String(row.amount)}
              isPayeeNoStatus
              hasStatus
              row={row as any}
            />
          );
        })}
        {rows.length > 3 && (
          <p
            className={classNames(styles.more_link, styles.link)}
            onClick={toggleIsExpanded}>
            {isExpanded
              ? t("lbl_contract_details_btn_show_less")
              : "+ " +
                String(rows.length - 3) +
                " " +
                t("lbl_order_created_more")}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={classNames(styles.tableWrapper, className)}>
      {hasErrors && (
        <div className={styles.errorFilterContainer}>
          <OutlinedButton
            onClick={toggleErrorFilter}
            className={classNames(styles.errorFilterButton, {
              [styles.activeFilter]: filterByErrors,
            })}>
            {filterByErrors
              ? t("lbl_show_all_parties")
              : t("lbl_show_only_errors")}
          </OutlinedButton>
          {hasErrors && (
            <p className={styles.errorCountText}>
              {t("lbl_total_error_count", { count: totalErrorCount })}
            </p>
          )}
        </div>
      )}
      <div className={styles.table}>
        <div className={styles.tableHeader}>
          <div
            className={classNames(styles.row, styles.headerRow)}
            style={{ gridTemplateColumns: gridTemplate }}>
            {enableBulkSelect && (
              <div className={classNames(styles.cell, styles.colSelect)}>
                {rows &&
                  rows?.length > 0 &&
                  !rows?.every(
                    (row) => row?.phoneNumber === clientAdminPhoneNumber
                  ) && (
                    <input
                      type="checkbox"
                      className={styles.selectCheckBox}
                      checked={selectAll}
                      onChange={handleSelectAll}
                      disabled={
                        !rows ||
                        rows.length === 0 ||
                        rows[0]?.id?.startsWith("empty")
                      }
                    />
                  )}
              </div>
            )}
            <div className={classNames(styles.cell, styles.colName)}>
              {t("lbl_name")}
            </div>
            <div className={classNames(styles.cell, styles.colPhone)}>
              {t("lbl_mobile_number")}
            </div>

            {showRole && (
              <div className={classNames(styles.cell, styles.colRole)}>
                {t("lbl_role")}
              </div>
            )}
            <div className={classNames(styles.cell, styles.colValue)}>
              {showInputField
                ? t("lbl_initial_value")
                : showCashoutTime
                ? t("lbl_transfer_amount")
                : t("lbl_value")}
            </div>
            {showFee && (
              <div className={classNames(styles.cell, styles.colWafyFee)}>
                {t("lbl_waffy_fees")}
              </div>
            )}
            {showCashoutTime && (
              <div className={classNames(styles.cell, styles.colWafyFee)}>
                {t("lbl_transfer_time")}
              </div>
            )}
            {showReferenceNumber && (
              <div className={classNames(styles.cell, styles.colWafyFee)}>
                {t("lbl_reference_number")}
              </div>
            )}
            {showTotal && (
              <div className={classNames(styles.cell, styles.colTotal)}>
                {t("lbl_total")}
              </div>
            )}
            {showActions && (
              <div className={classNames(styles.cell, styles.colActions)}>
                {t("lbl_actions")}
              </div>
            )}
            {showStatus && (
              <div className={classNames(styles.cell, styles.colStatus)}>
                {t("lbl_status")}
              </div>
            )}
            {showInputField && (
              <div className={classNames(styles.cell, styles.colStatus)}>
                {t("lbl_the_distribution")}
              </div>
            )}
          </div>
        </div>

        <div className={styles.tableBody}>
          {effectiveRows.map((row, index) => {
            const isZebra = index % 2 === 1;

            if (effectiveRows?.[0]?.id?.startsWith("empty") && index === 1)
              return (
                <p
                  key={`empty-1`}
                  style={{ marginTop: 0, textAlign: "center" }}>
                  {effectiveEmptyMessage}
                </p>
              );

            return (
              <div key={row.rowId} className={styles.group}>
                <div
                  className={classNames(
                    styles.row,
                    isZebra && styles.zebra,
                    hasErrorsForRow(row) && styles.errorRow
                  )}
                  style={{ gridTemplateColumns: gridTemplate }}>
                  {enableBulkSelect && (
                    <div className={classNames(styles.cell, styles.colSelect)}>
                      {rows &&
                        rows?.length > 0 &&
                        row?.phoneNumber !== clientAdminPhoneNumber && (
                          <input
                            type="checkbox"
                            className={styles.selectCheckBox}
                            checked={selectedRows.includes(
                              findPartyIndex(index)
                            )}
                            onChange={() => handleSelectRow(index)}
                            disabled={row.id?.startsWith("empty")}
                          />
                        )}
                    </div>
                  )}
                  <div className={classNames(styles.cell, styles.colName)}>
                    <div className={styles.cellWithError}>
                      <TooltipWrapper
                        id="name_id_tooltip"
                        content={
                          !!hasErrorsForRow(row) && !!getFieldError(row, "name")
                            ? getFieldError(row, "name") || ""
                            : row.name ||
                              (row.phoneNumber ? t("lbl_no_join") : "")
                        }>
                        <span
                          style={{
                            direction: isArabicOnly(row.name) ? "rtl" : "ltr",
                            textAlign: isArabicOnly(row.name) ? "start" : "end",
                            paddingLeft: "16px",
                          }}
                          className={classNames(styles.primaryText, {
                            [styles.fieldError]:
                              !!hasErrorsForRow(row) &&
                              !!getFieldError(row, "name"),
                          })}>
                          {row.name ||
                            (row.phoneNumber ? t("lbl_no_join") : "")}
                        </span>
                      </TooltipWrapper>
                    </div>
                  </div>
                  <div className={classNames(styles.cell, styles.colPhone)}>
                    <div className={styles.cellWithError}>
                      <TooltipWrapper
                        arabicDir
                        id="phone_number_id_tooltip"
                        content={
                          !!hasErrorsForRow(row) &&
                          !!getFieldError(row, "phoneNumber")
                            ? getFieldError(row, "phoneNumber") || ""
                            : row.phoneNumber || ""
                        }>
                        <span
                          style={{ direction: "ltr" }}
                          className={classNames(styles.monoText, {
                            [styles.fieldError]:
                              !!hasErrorsForRow(row) &&
                              !!getFieldError(row, "phoneNumber"),
                          })}>
                          {row?.id?.startsWith("empty")
                            ? ""
                            : row.phoneNumber || "-"}
                        </span>
                      </TooltipWrapper>
                    </div>
                  </div>
                  {showRole && (
                    <div className={classNames(styles.cell, styles.colRole)}>
                      <div className={styles.cellWithError}>
                        <TooltipWrapper
                          id="role_id_tooltip"
                          content={
                            !!hasErrorsForRow(row) &&
                            !!getFieldError(row, "role")
                              ? getFieldError(row, "role") || ""
                              : row?.role === ESenderRole.BROKER
                              ? t("lbl_arbitrator_title")
                              : row?.role || ""
                          }>
                          <span
                            className={classNames(styles.secondaryText, {
                              [styles.fieldError]:
                                !!hasErrorsForRow(row) &&
                                !!getFieldError(row, "role"),
                            })}>
                            {row?.role === ESenderRole.BROKER
                              ? t("lbl_arbitrator_title")
                              : row?.role || "-"}
                          </span>
                        </TooltipWrapper>
                      </div>
                    </div>
                  )}
                  <div className={classNames(styles.cell, styles.colValue)}>
                    <div className={styles.cellWithError}>
                      <TooltipWrapper
                        id="amount_id_tooltip"
                        content={
                          !!hasErrorsForRow(row) &&
                          !!getFieldError(row, "amount")
                            ? getFieldError(row, "amount") || ""
                            : row?.amount?.toLocaleString()
                        }>
                        <span
                          className={classNames(
                            styles.primaryText,
                            styles.amountText,
                            {
                              [styles.fieldError]:
                                !!hasErrorsForRow(row) &&
                                !!getFieldError(row, "amount"),
                            }
                          )}>
                          <span
                            style={{
                              color:
                                !!hasErrorsForRow(row) &&
                                !!getFieldError(row, "amount")
                                  ? "#e32a34"
                                  : "#000000",
                              marginLeft: "4px",
                            }}>
                            {row?.amount?.toLocaleString()}
                          </span>{" "}
                          {row?.amount || showInputField
                            ? t("lbl_saudi_currency")
                            : ""}
                        </span>
                      </TooltipWrapper>

                      {/* Show general errors if present */}
                      {hasErrorsForRow(row) &&
                        getFieldError(row, "general") && (
                          <div className={styles.fieldError}>
                            {getFieldError(row, "general")}
                          </div>
                        )}
                    </div>
                  </div>
                  {showFee && (
                    <div className={classNames(styles.cell, styles.colWafyFee)}>
                      {row.fee && (
                        <span
                          className={classNames(
                            styles.primaryText,
                            styles.amountText
                          )}>
                          <span style={{ color: "#00897B", marginLeft: "4px" }}>
                            {row?.fee?.toLocaleString()}
                          </span>{" "}
                          {t("lbl_saudi_currency")}
                        </span>
                      )}
                    </div>
                  )}
                  {showCashoutTime && (
                    <div className={classNames(styles.cell, styles.colWafyFee)}>
                      <span className={classNames(styles.normalText)}>
                        {row?.cashoutTime
                          ? new Date(row?.cashoutTime).toLocaleString("ar-EG", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true, // optional, ensures AM/PM (ص/م)
                            })
                          : "-"}
                      </span>
                    </div>
                  )}
                  {showReferenceNumber && (
                    <div
                      className={classNames(
                        styles.cell,
                        styles.colReferenceNumber
                      )}>
                      <div className={styles.cellWithError}>
                        <TooltipWrapper
                          id="bank_ref_number_id_tooltip"
                          content={
                            row?.referenceNumber ? row?.referenceNumber : "-"
                          }>
                          <span className={classNames(styles.normalText)}>
                            {row?.referenceNumber ? row?.referenceNumber : "-"}
                          </span>
                        </TooltipWrapper>
                      </div>
                    </div>
                  )}
                  {showTotal && (
                    <div className={classNames(styles.cell, styles.colTotal)}>
                      {row.total && (
                        <span
                          className={classNames(
                            styles.primaryText,
                            styles.amountText
                          )}>
                          <span style={{ color: "#0D44AD", marginLeft: "4px" }}>
                            {row?.total?.toLocaleString()}
                          </span>{" "}
                          {t("lbl_saudi_currency")}
                        </span>
                      )}
                    </div>
                  )}
                  {showActions && (
                    <div className={styles.cell}>
                      {rows && rows.length > 0 && handleEdit && (
                        <div className={styles.colActions}>
                          <button
                            type="button"
                            className={styles.linkButton}
                            onClick={handleEdit?.bind(
                              null,
                              findPartyIndex(index)
                            )}>
                            <img
                              src="/assets/images/svg/edit.svg"
                              alt="edit"
                              style={{ width: "17px", height: "17px" }}
                            />
                          </button>
                          {clientAdminPhoneNumber !== row?.phoneNumber &&
                            handleDelete && (
                              <button
                                type="button"
                                className={styles.linkButton}
                                onClick={() => {
                                  setSelectedIndex(index);
                                  setModalOpen(true);
                                }}>
                                <img
                                  src="/assets/images/svg/deleteAlt.svg"
                                  alt="delete"
                                  style={{ width: "17px", height: "17px" }}
                                />
                              </button>
                            )}
                        </div>
                      )}
                    </div>
                  )}

                  {showStatus && (
                    <div className={classNames(styles.cell, styles.colStatus)}>
                      <span
                        className={styles.statusPill}
                        style={{
                          background:
                            partiesStatuses?.[String(row?.status)]?.color,
                        }}>
                        <p
                          style={{
                            color:
                              partiesStatuses?.[String(row?.status)]?.textColor,
                          }}>
                          {partiesStatuses?.[String(row?.status)]?.title}
                        </p>
                      </span>
                    </div>
                  )}

                  {showInputField && inputFieldProps && (
                    <div className={classNames(styles.cell, styles.colField)}>
                      <TextInput
                        maxValue={inputFieldProps.maxAmount}
                        type={InputType.NUMBER}
                        handleChange={(e) => {
                          inputFieldProps.handleChange(
                            e,
                            row.rowId || "",
                            row.id || ""
                          );
                        }}
                        description={t("lbl_saudi_currency")}
                        defaultValue={row.formAmount}
                        isSettings
                        preventMaxValuePass
                        containerWidth="100%"
                        containerHeight="32px"
                        outerContainerWidth="100%"
                        placeholder="0.0"
                        style={{ fontSize: "0.75rem", direction: "ltr" }}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {showActions && (
          <div className={styles.tableFooter}>
            <div
              className={classNames(styles.row, styles.footerRow)}
              style={{ gridTemplateColumns: gridTemplate }}>
              {enableBulkSelect && rows && rows?.length > 0 && (
                <div className={classNames(styles.cell, styles.colSelect)} />
              )}
              <div className={classNames(styles.cell, styles.colName)}>
                <div className={styles.footerField} style={{ color: "black" }}>
                  {t("lbl_total")}
                </div>
              </div>
              <div className={classNames(styles.cell, styles.colPhone)} />
              {showRole && (
                <div className={classNames(styles.cell, styles.colRole)} />
              )}
              <div className={classNames(styles.cell, styles.colValue)}>
                <div className={styles.footerField}>
                  <span style={{ color: "#0D44AD", marginLeft: "4px" }}>
                    {sumColumn("amount").toLocaleString()}
                  </span>{" "}
                  {t("lbl_saudi_currency")}
                </div>
              </div>
              {showFee && (
                <div className={classNames(styles.cell, styles.colWafyFee)}>
                  <div className={styles.footerField}>
                    <span style={{ color: "#00897B", marginLeft: "4px" }}>
                      {sumColumn("fee").toLocaleString()}
                    </span>{" "}
                    {t("lbl_saudi_currency")}
                  </div>
                </div>
              )}
              {showTotal && (
                <div className={classNames(styles.cell, styles.colTotal)}>
                  <div className={styles.footerField}>
                    <span style={{ color: "#0D44AD", marginLeft: "4px" }}>
                      {sumColumn("total").toLocaleString()}
                    </span>{" "}
                    {t("lbl_saudi_currency")}
                  </div>
                </div>
              )}
              <div className={classNames(styles.cell, styles.colActions)} />
            </div>
          </div>
        )}
      </div>
      <ConfirmationModal
        handleClose={() => setModalOpen(false)}
        handleConfirm={() => {
          setModalOpen(false);
          handleDelete?.(findPartyIndex(selectedIndex));
        }}
        horizontalButtons
        modalOpen={modalOpen}
        icon={<DeleteResource />}
        confirmText={t("remove")}
        cancelText={t("lbl_delete_transaction_back_btn")}
        containerStyle={styles.modalContainer}
        title={"هل أنت متأكد من الحذف؟"}
        description={`سيتم حذف الطرف : ${rows?.[selectedIndex]?.name}`}
      />
    </div>
  );
};

export default PartyTable;
